import { createContext, useContext, useState, useEffect, useRef, ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { isFrenchPath, stripLocalePrefix, addFrPrefix } from "@/hooks/useLocalePath";

type Language = "fr" | "en";

interface Translations {
  // SEO
  seoTitle: string;
  seoDescription: string;
  
  // Header
  quadrant: string;
  pillars: string;
  methodology: string;
  
  // Hero
  badge: string;
  heroTitle1: string;
  heroTitle2: string;
  heroSubtitle: string;
  searchPlaceholder: string;
  searchPlaceholderMobile: string;
  brandsAnalyzed: string;
  ethicalPillars: string;
  transparent: string;
  noResults: string;
  
  // Inspirational quote
  quoteTitle: string;
  quoteText: string;
  quoteAuthor: string;
  
    // Trust section
    trustTitle: string;
    trustPublicData: string;
    trustPublicDataDesc: string;
    trustIndependent: string;
    trustIndependentDesc: string;
    trustProprietary: string;
    trustProprietaryDesc: string;
  
  // Quadrant
  quadrantTitle: string;
  quadrantSubtitle: string;
  eclaireurs: string;
  eclaireursDesc: string;
  pionniersGlobaux: string;
  pionniersGlobauxDesc: string;
  acteursProximite: string;
  acteursProximiteDesc: string;
  retardataires: string;
  retardatairesDesc: string;
  xAxis: string;
  yAxis: string;
  howToRead: string;
  howToReadTextX: string;
  howToReadTextY: string;
  clickBrand: string;
  
  // Pillars
  pillarsTitle: string;
  pillarsSubtitle: string;
  human: string;
  humanDesc: string;
  manufacturing: string;
  manufacturingDesc: string;
  product: string;
  productDesc: string;
  logistics: string;
  logisticsDesc: string;
  citizenship: string;
  citizenshipDesc: string;
  
  // Brand page
  backToQuadrant: string;
  wiseCompassScore: string;
  excellent: string;
  good: string;
  average: string;
  poor: string;
  veryPoor: string;
  scoresByPillar: string;
  radarTitle: string;
  knowThisBrand: string;
  helpImprove: string;
  contribute: string;
  brandNotFound: string;
  backHome: string;
  
  // Footer
  footerDesc: string;
  navigation: string;
  home: string;
  about: string;
  legal: string;
  privacyPolicy: string;
  termsOfUse: string;
  madeWith: string;
  inFrance: string;
}

const translations: Record<Language, Translations> = {
  fr: {
    seoTitle: "Décrypter les marques de mode | The Wise Compass",
    seoDescription: "Chaque achat est un vote. The Wise Compass décrypte l'impact des marques pour mettre fin au greenwashing et trouver des alternatives éthiques.",
    quadrant: "Quadrant",
    pillars: "Piliers",
    methodology: "Méthodologie",
    badge: "Consommez selon vos valeurs",
    heroTitle1: "Ne laissez plus le marketing",
    heroTitle2: "décider pour vous",
    heroSubtitle: "Découvrez l'impact réel de vos marques. Notre indicateur analyse la mode sur 5 piliers éthiques pour des choix éclairés.",
    searchPlaceholder: "Quelle marque voulez-vous passer au verdict ?",
    searchPlaceholderMobile: "Rechercher une marque",
    brandsAnalyzed: "Marques analysées",
    ethicalPillars: "Piliers éthiques",
    transparent: "Transparent",
    noResults: "Aucune marque trouvée pour",
    quoteTitle: "Votre pouvoir d'achat, votre responsabilité",
    quoteText: "Chaque achat est un vote. Votre porte-monnaie façonne la société de demain. Avec un grand pouvoir vient une grande responsabilité.",
    quoteAuthor: "Ensemble, construisons un monde plus juste",
    trustTitle: "Pourquoi nous faire confiance ?",
    trustPublicData: "Données publiques",
    trustPublicDataDesc: "Chaque score est basé sur des sources publiques disponibles en libre service sur internet.",
    trustIndependent: "100% Indépendant",
    trustIndependentDesc: "Aucun partenariat commercial avec les marques. Notre seul objectif : vous informer.",
    trustProprietary: "Méthodologie propriétaire",
    trustProprietaryDesc: "Notre algorithme synthétise expertise humaine et IA pour transformer la donnée complexe en transparence.",
    quadrantTitle: "Le Quadrant des Marques",
    quadrantSubtitle: "Visualisez d'un coup d'œil où se positionnent les marques selon l'intégrité de leurs produits et leur cohérence territoriale.",
    eclaireurs: "Les Éclaireurs",
    eclaireursDesc: "Excellent produit et ancrage local fort.",
    pionniersGlobaux: "Les Pionniers Globaux",
    pionniersGlobauxDesc: "Produit irréprochable mais modèle \"Pure Player\" web sans ancrage physique local.",
    acteursProximite: "Les Acteurs de Proximité",
    acteursProximiteDesc: "Fort impact local mais produit à améliorer.",
    retardataires: "Les Retardataires",
    retardatairesDesc: "Faible qualité et opacité fiscale.",
    xAxis: "Intégrité de l'Objet →",
    yAxis: "Cohérence du Modèle →",
    howToRead: "Comment lire le quadrant ?",
    howToReadTextX: "L'Axe X mesure l'**Intégrité de l'Objet** (Le \"Quoi\") : la valeur intrinsèque du produit et son **impact direct sur le vivant**.",
    howToReadTextY: "L'Axe Y mesure la **Cohérence du Modèle** (Le \"Comment\") : la structure de l'entreprise et son **ancrage dans la société**.",
    clickBrand: "Cliquez sur une marque pour voir le détail de ses scores sur les 5 piliers.",
    pillarsTitle: "Les 5 Axes du Wise Compass",
    pillarsSubtitle: "Notre méthodologie analyse chaque marque sur 5 dimensions essentielles pour une consommation responsable.",
    human: "Éthique",
    humanDesc: "Droit du travail · Traçabilité · Labels sociaux",
    manufacturing: "Écologie",
    manufacturingDesc: "Matières · Santé · Climat",
    product: "Durabilité",
    productDesc: "Surproduction · Qualité · Réparation",
    logistics: "Proximité",
    logisticsDesc: "Trajet · Zéro Avion · Économie locale",
    citizenship: "Transparence",
    citizenshipDesc: "Fiscalité · Propriété · Anti-Bullshit",
    backToQuadrant: "Retour au quadrant",
    wiseCompassScore: "Score Wise Compass",
    excellent: "Excellent",
    good: "Bon",
    average: "Moyen",
    poor: "Faible",
    veryPoor: "Très faible",
    scoresByPillar: "Détail des scores par pilier",
    radarTitle: "Vue d'ensemble des scores",
    knowThisBrand: "Vous connaissez cette marque ?",
    helpImprove: "Aidez-nous à améliorer nos données en partageant votre expérience.",
    contribute: "Contribuer",
    brandNotFound: "Marque non trouvée",
    backHome: "Retour à l'accueil",
    footerDesc: "Nous aidons les consommateurs à faire des choix alignés avec leurs valeurs grâce à notre analyse éthique des marques de mode.",
    navigation: "Navigation",
    home: "Accueil",
    about: "À propos",
    legal: "Légal",
    privacyPolicy: "Politique de confidentialité",
    termsOfUse: "Conditions d'utilisation",
    madeWith: "Fait avec",
    inFrance: "en France",
  },
  en: {
    seoTitle: "Decoding Fashion Brands | The Wise Compass",
    seoDescription: "Every purchase is a vote. The Wise Compass deciphers brand impact to end greenwashing and find ethical and responsible alternatives.",
    quadrant: "Quadrant",
    pillars: "Pillars",
    methodology: "How It Works",
    badge: "Shop your values",
    heroTitle1: "Cut through the",
    heroTitle2: "marketing noise",
    heroSubtitle: "Uncover what brands really stand for. We rate fashion across 5 ethical pillars so you can shop smarter.",
    searchPlaceholder: "Search any brand to see its true score",
    searchPlaceholderMobile: "Search a brand",
    brandsAnalyzed: "Brands rated",
    ethicalPillars: "Ethical pillars",
    transparent: "Fully transparent",
    noResults: "No results for",
    quoteTitle: "Your money, your impact",
    quoteText: "Every purchase is a vote. Your spending shapes the world we live in. With great power comes great responsibility.",
    quoteAuthor: "Let's build a better future together",
    trustTitle: "Why trust us?",
    trustPublicData: "Open-Source Data",
    trustPublicDataDesc: "Every score is based on publicly available sources, freely accessible online.",
    trustIndependent: "Completely Independent",
    trustIndependentDesc: "Zero brand partnerships. We're here for you, not them.",
    trustProprietary: "Proprietary Methodology",
    trustProprietaryDesc: "Our algorithm blends human expertise and AI to turn complex data into clear transparency.",
    quadrantTitle: "The Brand Quadrant",
    quadrantSubtitle: "See where brands really stand on product ethics and local impact—at a glance.",
    eclaireurs: "The Trailblazers",
    eclaireursDesc: "Excellent product and strong local roots.",
    pionniersGlobaux: "Global Pioneers",
    pionniersGlobauxDesc: "Flawless product but 'Pure Player' web model with no local footprint.",
    acteursProximite: "Local Champions",
    acteursProximiteDesc: "Strong local impact but product needs improvement.",
    retardataires: "The Laggards",
    retardatairesDesc: "Low quality and fiscal opacity.",
    xAxis: "Object Integrity →",
    yAxis: "Model Coherence →",
    howToRead: "How to read this chart",
    howToReadTextX: "The X-Axis measures **Object Integrity** (The \"What\"): the intrinsic value of the product and its **direct impact on living beings**.",
    howToReadTextY: "The Y-Axis measures **Model Coherence** (The \"How\"): the company's structure and its **rootedness in society**.",
    clickBrand: "Tap any brand to dive into its full breakdown across all 5 pillars.",
    pillarsTitle: "The 5 Axes of the Wise Compass",
    pillarsSubtitle: "Every brand is scored across 5 key areas that matter for conscious shopping.",
    human: "Ethics",
    humanDesc: "Labor rights · Traceability · Social certifications",
    manufacturing: "Ecology",
    manufacturingDesc: "Materials · Health · Climate",
    product: "Durability",
    productDesc: "Overproduction · Quality · Repair",
    logistics: "Proximity",
    logisticsDesc: "Distance · No Air Freight · Local economy",
    citizenship: "Transparency",
    citizenshipDesc: "Tax policy · Ownership · Anti-Greenwashing",
    backToQuadrant: "Back to quadrant",
    wiseCompassScore: "Wise Compass Score",
    excellent: "Excellent",
    good: "Good",
    average: "Fair",
    poor: "Poor",
    veryPoor: "Very poor",
    scoresByPillar: "Breakdown by pillar",
    radarTitle: "At a glance",
    knowThisBrand: "Know this brand?",
    helpImprove: "Got firsthand experience? Help us keep our data accurate.",
    contribute: "Share feedback",
    brandNotFound: "Brand not found",
    backHome: "Go home",
    footerDesc: "We help shoppers make choices that align with their values through honest, data-driven brand ratings.",
    navigation: "Explore",
    home: "Home",
    about: "About",
    legal: "Legal",
    privacyPolicy: "Privacy Policy",
    termsOfUse: "Terms of Service",
    madeWith: "Made with",
    inFrance: "in France",
  }
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
  setPathOverrides: (overrides: { fr?: string; en?: string } | null) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const detectBrowserLanguage = (): Language => {
  const browserLang = navigator.language || (navigator as any).userLanguage;
  return browserLang?.toLowerCase().startsWith('fr') ? 'fr' : 'en';
};

const LANGUAGE_STORAGE_KEY = 'wise-compass-language';

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const pathOverridesRef = useRef<{ fr?: string; en?: string } | null>(null);

  // Determine initial language from URL first, then localStorage, then browser
  const [language, setLanguageState] = useState<Language>(() => {
    if (isFrenchPath(pathname)) return 'fr';
    if (pathname === '/') {
      const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (saved === 'fr') return 'fr';
      if (saved === 'en') return 'en';
      return detectBrowserLanguage();
    }
    return 'en';
  });

  // Sync language from URL changes (e.g. browser back/forward)
  useEffect(() => {
    const urlLang = isFrenchPath(pathname) ? 'fr' : 'en';
    if (urlLang !== language) {
      setLanguageState(urlLang);
    }
  }, [pathname]);

  // On first load, if browser/localStorage says FR but we're on root, redirect to /fr
  useEffect(() => {
    if (language === 'fr' && !isFrenchPath(pathname)) {
      const frPath = pathname === '/' ? '/fr' : `/fr${pathname}`;
      navigate(frPath, { replace: true });
    }
  }, []); // only on mount

  // Allow pages (e.g. blog articles) to register language-specific paths so the
  // language toggle can navigate to the correct localized slug.
  const setPathOverrides = (overrides: { fr?: string; en?: string } | null) => {
    pathOverridesRef.current = overrides;
  };

  // Public setLanguage that also navigates
  const setLanguage = (lang: Language) => {
    if (lang === language) return;
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    setLanguageState(lang);

    const override = pathOverridesRef.current?.[lang];
    if (override) {
      navigate(override, { replace: true });
      return;
    }

    const basePath = stripLocalePrefix(pathname);
    if (lang === 'fr') {
      navigate(addFrPrefix(basePath), { replace: true });
    } else {
      navigate(basePath, { replace: true });
    }
  };

  // Save preference and update HTML lang
  useEffect(() => {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    document.documentElement.lang = language;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t: translations[language], setPathOverrides }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
